import {
    CoachableMetric,
    CoachingType,
    GoalStatus,
} from '../../coaching-base/general';
import { MetricDataItem } from '../../insight/performance-data/metric.pd.model';
import { createReducer } from '../../store.model';
import { FocusKPI } from '../models/commitment.model';

import { goalsCollectionTransformer } from './goals-collection.transformer';
import {
    ActionType,
    GoalCommitmentAddStateGoalAction,
    GoalCommitmentFillFocusKPIPerformanceAction,
    GoalCommitmentInitAction,
    GoalCommitmentInitResultAction,
    GoalCommitmentMergeChangedFocusKPIsAction,
    GoalCommitmentRetrieveDataAction,
    GoalCommitmentRetrieveDataCancelAction,
    GoalCommitmentRetrieveDataResultAction,
    GoalCommitmentRetrieveGoalEventHistoryAction,
    GoalCommitmentRetrieveGoalEventHistoryResultAction,
    GoalCommitmentRetrieveGoalHistoryAction,
    GoalCommitmentRetrieveGoalHistoryResultAction,
    GoalCommitmentSortGoalsAction,
    GoalCommitmentUpdateStateGoalAction,
    LoadGoalsResult,
    RootClearStateAction,
} from './goals.commitment.action';
import { sortGoals } from './goals.commitment.function';
import {
    defaultGoalCommitmentState,
    GoalCommitmentState,
} from './goals.commitment.state';
import { overrideGoals } from './override-goals.function';

const { builder, reducer } = createReducer<ActionType, GoalCommitmentState>(
    defaultGoalCommitmentState(),
);

builder
    .add<GoalCommitmentInitAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__INIT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    init: true,
                },
            };
        },
    )
    .add<GoalCommitmentInitResultAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__INIT_RESULT,
        (state, action) => {
            // TODO consolidate with tags.function makeRows()
            const onlyCoachingTypes = [
                CoachingType.UNCOACHED,
                CoachingType.COACHED,
                CoachingType.NON_OPPORTUNITY,
                null,
                undefined,
            ];
            const nextMetrics = (
                action.payload.metrics || ([] as CoachableMetric[])
            ).filter((e) => onlyCoachingTypes.includes(e.coachTypeId));

            /* don't adjust for pinned metrics since a placeholder draft goal is typically added elsewhere */

            return {
                ...state,
                pending: {
                    ...state.pending,
                    init: false,
                },
                metrics: nextMetrics,
                focusSummarySet: action.payload.focusSummarySet,
            };
        },
    );

builder
    .add<GoalCommitmentRetrieveDataAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    data: true,
                },
                metricData: undefined,
                lastMetricDataItem: undefined,
            };
        },
    )
    .add<GoalCommitmentRetrieveDataResultAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_RESULT,
        (state, action) => {
            let lastMetricDataItem: MetricDataItem;
            if (action.payload.metricData) {
                const sorted = [...action.payload.metricData.metrics].sort(
                    (a, b) => b.eventDate.getTime() - a.eventDate.getTime(),
                );
                lastMetricDataItem = sorted.length !== 0 ? sorted[0] : null;
            }

            return {
                ...state,
                pending: {
                    ...state.pending,
                    data: false,
                },
                metricData: action.payload.metricData,
                lastMetricDataItem,
            };
        },
    )
    .add<GoalCommitmentRetrieveDataCancelAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_DATA_CANCEL,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    data: false,
                },
                metricData: undefined,
                lastMetricDataItem: undefined,
            };
        },
    );

builder.add<GoalCommitmentSortGoalsAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__SORT_GOALS,
    (state, action) => {
        const commitmentKpis = state.commitmentKpis
            ? [...state.commitmentKpis]
            : [];
        const goalsFromOtherCommitments = state.goalsFromOtherCommitments
            ? [...state.goalsFromOtherCommitments]
            : [];
        const goalsSortDirection = action.payload.direction;

        commitmentKpis.sort((x, y) => sortGoals(x, y, goalsSortDirection));
        goalsFromOtherCommitments.sort((x, y) =>
            sortGoals(x, y, goalsSortDirection),
        );

        return {
            ...state,
            commitmentKpis,
            goalsFromOtherCommitments,
            goalsSortDirection,
        };
    },
);

builder.add<GoalCommitmentAddStateGoalAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__ADD_STATE_GOAL,
    (state, action) => {
        // add 1 goal to the CommitmentKPIs state object
        let commitmentKpis = [...state.commitmentKpis];
        const index = commitmentKpis.findIndex(
            (e) =>
                e.metricId === action.payload.goal.metricId &&
                e.statusId !== GoalStatus.CLOSED,
        );
        if (index !== -1 && action.payload.goal.goalId == null) {
            commitmentKpis.splice(index, 1, {
                ...commitmentKpis[index],
                ...action.payload.goal,
            });
        } else {
            commitmentKpis = [...commitmentKpis, action.payload.goal];
        }
        commitmentKpis.sort((x, y) =>
            sortGoals(x, y, state.goalsSortDirection),
        );

        return {
            ...state,
            commitmentKpis,
        };
    },
);

builder.add<GoalCommitmentUpdateStateGoalAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__UPDATE_STATE_GOAL,
    (state, action) => {
        const commitmentKpis = [...state.commitmentKpis];
        const focusKpis = [...state.focusKpis];

        if (action.payload.isPlaceholderMetric) {
            // remove placeholder metric from focusKpis
            focusKpis.splice(action.payload.index, 1);
        } else {
            if (action.payload.goalStatusId) {
                if (action.payload.goalStatusId === GoalStatus.DRAFT) {
                    commitmentKpis[action.payload.index].statusId =
                        GoalStatus.DELETED;
                } else if (action.payload.goalStatusId === GoalStatus.ACTIVE) {
                    commitmentKpis[action.payload.index].statusId =
                        GoalStatus.PENDING_CLOSE;
                }
            } else {
                // replace 1 goal from the CommitmentKPIs state object
                commitmentKpis.splice(action.payload.index, 1, {
                    ...commitmentKpis[action.payload.index],
                    ...action.payload.goal,
                });
            }
            commitmentKpis.sort((x, y) =>
                sortGoals(x, y, state.goalsSortDirection),
            );
        }

        return {
            ...state,
            commitmentKpis,
            focusKpis,
        };
    },
);

builder
    .add<GoalCommitmentRetrieveGoalHistoryAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    history: true,
                },
            };
        },
    )
    .add<GoalCommitmentRetrieveGoalHistoryResultAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_HISTORY_RESULT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    history: false,
                },
                goalHistory: action.payload.goalHistory,
                goalDataPoints: action.payload.goalDataPoints,
            };
        },
    );

builder.add<LoadGoalsResult>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__LOAD_GOALS_RESULT,
    (state, action) => {
        const collection = action.payload.goalsCollection || {};

        const nextCollection = goalsCollectionTransformer(
            collection.commitmentKpis,
            action.payload.focusKpis,
            action.payload.otherCommitmentKpis,
        );

        overrideGoals(nextCollection);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const commitmentKpis =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (nextCollection.commitmentKpis &&
                nextCollection.commitmentKpis.sort((x, y) =>
                    sortGoals(x, y, state.goalsSortDirection),
                )) ||
            [];
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        const goalsFromOtherCommitments =
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            (nextCollection.goalsFromOtherCommitments &&
                nextCollection.goalsFromOtherCommitments.sort((x, y) =>
                    sortGoals(x, y, state.goalsSortDirection),
                )) ||
            [];
        return {
            ...state,
            commitmentKpis,
            goalsFromOtherCommitments,
        };
    },
);

builder.add<GoalCommitmentMergeChangedFocusKPIsAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__MERGE_CHANGED_FOCUS_KPIS,
    (state, action) => {
        const focusKpis = state.focusKpis || [];
        const commitmentKpis = state.commitmentKpis || [];
        let metricIds: number[] = [];
        const lastLinkedObs = action.payload.lastLinkedObservation;
        const finalFocusKpis: FocusKPI[] = [];

        if (lastLinkedObs) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            lastLinkedObs.focusContainers &&
                lastLinkedObs.focusContainers.forEach((e) => {
                    metricIds.push(
                        ...(e.metrics || []).map((eee) => eee.metricId),
                    );
                });
        }

        if (action.payload.placeholderMetrics) {
            metricIds = metricIds.concat(action.payload.placeholderMetrics);
        }

        metricIds = Array.from(new Set(metricIds));
        metricIds.forEach((e) => {
            let existing = commitmentKpis.find((ee) => ee.metricId === e);
            if (existing) {
                return;
            }

            existing = focusKpis.find((ee) => ee.metricId === e);
            if (existing) {
                finalFocusKpis.push(existing);
                return;
            }

            const metric = state.metrics.find((ee) => ee.metricId === e);
            if (!metric) {
                console.warn(`metricId ${e} not found`);
                // no return
            }

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            finalFocusKpis.push({
                metricId: e,
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                goal_Kpi: metric && metric.metricName,
                tracking_id: Math.random(),
            } as FocusKPI);
        });

        return {
            ...state,
            focusKpis: finalFocusKpis,
        };
    },
);

builder.add<GoalCommitmentRetrieveGoalEventHistoryAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY,
    (state, action) => {
        return {
            ...state,
            pending: {
                ...state.pending,
                events: true,
            },
        };
    },
);

builder
    .add<GoalCommitmentFillFocusKPIPerformanceAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__FILL_FOCUS_KPI_PERFORMANCE,
        (state, action) => {
            const focusKpis = state.focusKpis;

            if (focusKpis) {
                const metrics = action.payload.metrics;
                focusKpis.forEach((a) => {
                    // only modify data if it doesn't have any of the below fields
                    if (!a.actual && !a.target && !a.percentToTarget) {
                        const metricdataitem = metrics.find(
                            (m) => m.id === a.metricId,
                        );
                        if (metricdataitem) {
                            a.actual = metricdataitem.actual;
                            a.target = metricdataitem.target;
                            a.percentToTarget = metricdataitem.percentToTarget;
                            a.goal_Kpi = metricdataitem.name;
                        }
                    }
                });
            }

            return {
                ...state,
                focusKpis, // form
            };
        },
    )
    .add<GoalCommitmentRetrieveGoalEventHistoryResultAction>(
        ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT__RETRIEVE_GOAL_EVENT_HISTORY_RESULT,
        (state, action) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    events: false,
                },
                goalEventDataPoints: action.payload.goalEventDataPoints,
            };
        },
    );

builder.add<RootClearStateAction>(
    ActionType.COACHING_COMMITMENT__GOAL_COMMITMENT_ROOT__CLEAR_STATE,
    () => {
        return defaultGoalCommitmentState();
    },
);

export default {
    goalCommitment: reducer,
};
